<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import clubService from '../services/club.service'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

// Get club UUID from route
const clubUuid = computed(() => route.params.uuid as string)

// Get current role
const currentRole = computed(() => authStore.initData?.persons?.current?.role || null)

const isDemo = computed(() => authStore.isDemo)

// Check if user can manage bank accounts (OWNER or SUPERVISOR)
const canManageBankAccounts = computed(() => {
  if (isDemo.value) return false
  return currentRole.value === 'OWNER' || currentRole.value === 'SUPERVISOR'
})

// State
const bankAccounts = ref<any[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const showDeleteModal = ref(false)
const accountToDelete = ref<string | null>(null)
const openDropdownId = ref<string | null>(null)
const openAccordionId = ref<string | null>(null)

// Format account number (26 digits to XX XXXX XXXX XXXX XXXX XXXX XXXX)
const formatAccountNumber = (accountNumber: string): string => {
  if (!accountNumber) return ''
  // Remove all spaces first
  const cleaned = accountNumber.replace(/\s/g, '')
  // Add spaces: 2 digits, then 4 digits repeated
  return cleaned.replace(/(\d{2})(\d{4})(\d{4})(\d{4})(\d{4})(\d{4})(\d{4})/, '$1 $2 $3 $4 $5 $6 $7')
}

// Fetch bank accounts
const fetchBankAccounts = async () => {
  if (!clubUuid.value) {
    error.value = t('bankAccounts.errorNoClubUuid')
    return
  }

  loading.value = true
  error.value = null

  try {
    const response = await clubService.getBankAccounts(clubUuid.value)
    // Map backend data to frontend format
    bankAccounts.value = (response.bank_accounts || []).map((account: any) => ({
      ...account,
      name: account.account_name,
      account_number_formatted: formatAccountNumber(account.account_number)
    }))
  } catch (err: any) {
    console.error('Fetch bank accounts error:', err)
    error.value = err.response?.data?.message || t('bankAccounts.errorFetchingBankAccounts')
  } finally {
    loading.value = false
  }
}

// Toggle dropdown
const toggleDropdown = (event: Event, accountId: string) => {
  event.stopPropagation()

  if (openDropdownId.value === accountId) {
    openDropdownId.value = null
  } else {
    openDropdownId.value = accountId
  }
}

// Close all dropdowns when clicking outside
const closeAllDropdowns = () => {
  openDropdownId.value = null
}

// Toggle accordion (mobile)
const toggleAccordion = (accountUuid: string) => {
  if (openAccordionId.value === accountUuid) {
    openAccordionId.value = null
  } else {
    openAccordionId.value = accountUuid
  }
}

// Open delete modal
const confirmDelete = (accountUuid: string) => {
  accountToDelete.value = accountUuid
  showDeleteModal.value = true
  openDropdownId.value = null
}

// Close delete modal
const closeDeleteModal = () => {
  accountToDelete.value = null
  showDeleteModal.value = false
}

// Delete account
const deleteAccount = async () => {
  if (!accountToDelete.value || !clubUuid.value) return

  try {
    await clubService.deleteBankAccount(clubUuid.value, accountToDelete.value)

    toastStore.success(t('bankAccounts.bankAccountDeleted'))
    closeDeleteModal()
    await fetchBankAccounts()
  } catch (err: any) {
    console.error('Delete bank account error:', err)
    toastStore.error(err.response?.data?.message || t('bankAccounts.errorDeletingBankAccount'))
  }
}

// Go back to club page
const goBack = () => {
  router.push(`/club/${clubUuid.value}`)
}

// Navigate to add bank account
const goToAddBankAccount = () => {
  router.push(`/club/${clubUuid.value}/bank-accounts/create`)
}

// Navigate to edit bank account
const goToEditBankAccount = (accountUuid: string) => {
  router.push(`/club/${clubUuid.value}/bank-accounts/${accountUuid}/edit`)
}

// Fetch data on mount
onMounted(() => {
  fetchBankAccounts()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen" @click="closeAllDropdowns">
    <div class="w-full px-4 py-4">
      <!-- Back Button -->
      <div class="mb-4">
        <button
          @click="goBack"
          class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('bankAccounts.backToClubPage') }}
        </button>
      </div>

      <!-- Demo restriction banner -->
      <div v-if="isDemo" class="mb-4 bg-amber-50 border-2 border-amber-300 rounded p-4">
        <p class="text-sm font-semibold text-amber-800">{{ t('demo.restrictions.bankAccountsTitle') }}</p>
        <p class="text-sm text-amber-700 mt-1">{{ t('demo.restrictions.bankAccountsText') }}</p>
      </div>

      <!-- Page Header -->
      <div class="mb-4 flex justify-between items-center">
        <h1 class="text-2xl font-bold text-gray-900">
          {{ t('bankAccounts.title') }} <span class="text-lg font-normal text-gray-600">({{ bankAccounts.length }})</span>
        </h1>
        <button
          v-if="canManageBankAccounts"
          @click="goToAddBankAccount"
          class="px-4 py-2 bg-green-600 text-sm font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 flex items-center gap-2"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
          </svg>
          <span class="lg:inline hidden">{{ t('bankAccounts.addBankAccount') }}</span>
          <span class="lg:hidden inline">{{ t('bankAccounts.addBankAccountShort') }}</span>
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="bg-white border-2 border-gray-300 rounded shadow-sm p-8 text-center">
        <p class="text-gray-600">{{ t('bankAccounts.loading') }}</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border-2 border-red-300 rounded shadow-sm p-4">
        <p class="text-red-800">{{ error }}</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="bankAccounts.length === 0" class="bg-white border-2 border-gray-300 rounded shadow-sm p-8 text-center">
        <p class="text-gray-600">{{ t('bankAccounts.emptyState') }}</p>
      </div>

      <!-- Desktop Table -->
      <div v-else class="hidden md:block bg-white border-2 border-gray-300 rounded shadow-sm overflow-y-visible" style="overflow-x: clip;">
        <div>
          <table class="w-full text-sm">
            <thead class="bg-gray-100 border-b-2 border-gray-300">
              <tr>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('bankAccounts.accountName') }}</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('bankAccounts.bankName') }}</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('bankAccounts.accountNumber') }}</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">SWIFT/BIC</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('bankAccounts.currency') }}</th>
                <th v-if="canManageBankAccounts" class="px-4 py-3 text-right font-semibold text-gray-700 sticky right-0 bg-gray-100">{{ t('common.actions') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr
                v-for="(account, index) in bankAccounts"
                :key="account.uuid"
                class="hover:bg-gray-50"
              >
                <td class="px-4 py-3 font-medium text-gray-900">{{ account.name }}</td>
                <td class="px-4 py-3 text-gray-700">{{ account.bank_name }}</td>
                <td class="px-4 py-3 text-gray-700 font-mono">{{ account.account_number_formatted }}</td>
                <td class="px-4 py-3 text-gray-700 font-mono">{{ account.swift || '-' }}</td>
                <td class="px-4 py-3 text-gray-700">{{ account.currency || 'PLN' }}</td>
                <td v-if="canManageBankAccounts" class="px-4 py-3 sticky right-0 bg-white text-right" style="position: relative;">
                  <button
                    @click.stop="toggleDropdown($event, `desktop-${account.uuid}`)"
                    class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                  >
                    {{ t('common.actions') }}
                  </button>
                  <div
                    :class="['custom-dropdown', 'dropdown-bottom', { 'show': openDropdownId === `desktop-${account.uuid}` }]"
                    @click.stop
                  >
                    <button @click="goToEditBankAccount(account.uuid)">{{ t('common.edit') }}</button>
                    <button class="delete-action" @click="confirmDelete(account.uuid)">{{ t('common.delete') }}</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Mobile Accordion -->
      <div class="md:hidden space-y-2">
        <div
          v-for="account in bankAccounts"
          :key="account.uuid"
          class="bg-white border-2 border-gray-300 rounded shadow-sm"
        >
          <!-- Accordion Header -->
          <div class="p-3 cursor-pointer" @click="toggleAccordion(account.uuid)">
            <div class="flex items-center justify-between gap-2">
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-gray-900">{{ account.name }}</div>
              </div>
              <div class="flex items-center gap-2">
                <div v-if="canManageBankAccounts" style="position: relative;">
                  <button
                    @click.stop="toggleDropdown($event, `mobile-${account.uuid}`)"
                    class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                  >
                    {{ t('common.actions') }}
                  </button>
                  <div
                    :class="['custom-dropdown', 'dropdown-bottom', { 'show': openDropdownId === `mobile-${account.uuid}` }]"
                    @click.stop
                  >
                    <button @click="goToEditBankAccount(account.uuid)">{{ t('common.edit') }}</button>
                    <button class="delete-action" @click="confirmDelete(account.uuid)">{{ t('common.delete') }}</button>
                  </div>
                </div>
                <svg
                  :class="['w-5 h-5 text-gray-600 transition-transform', { 'rotate-180': openAccordionId === account.uuid }]"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
                </svg>
              </div>
            </div>
          </div>
          <!-- Accordion Content -->
          <div
            v-show="openAccordionId === account.uuid"
            class="border-t-2 border-gray-300 p-3"
          >
            <div class="space-y-2 text-xs">
              <div class="flex justify-between">
                <span class="font-semibold text-gray-700">{{ t('bankAccounts.bankName') }}:</span>
                <span class="text-gray-600">{{ account.bank_name }}</span>
              </div>
              <div class="flex flex-col">
                <span class="font-semibold text-gray-700 mb-1">{{ t('bankAccounts.accountNumber') }}:</span>
                <span class="text-gray-600 font-mono">{{ account.account_number_formatted }}</span>
              </div>
              <div v-if="account.swift" class="flex flex-col">
                <span class="font-semibold text-gray-700 mb-1">SWIFT/BIC:</span>
                <span class="text-gray-600 font-mono">{{ account.swift }}</span>
              </div>
              <div class="flex justify-between">
                <span class="font-semibold text-gray-700">{{ t('bankAccounts.currency') }}:</span>
                <span class="text-gray-600">{{ account.currency || 'PLN' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>

  <!-- Delete Confirmation Modal -->
  <div
    v-if="showDeleteModal"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="closeDeleteModal"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
      <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ t('bankAccounts.deleteConfirmation') }}</h3>
      <p class="text-sm text-gray-600 mb-4">{{ t('bankAccounts.deleteConfirmationMessage') }}</p>
      <div class="flex justify-end gap-3">
        <button
          @click="closeDeleteModal"
          class="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200 border border-gray-300"
        >
          {{ t('common.cancel') }}
        </button>
        <button
          @click="deleteAccount"
          class="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700"
        >
          {{ t('common.delete') }}
        </button>
      </div>
    </div>
  </div>
</template>
